// backoffice.settings.staff-settings.user-roles (legacy UserRolesIndex/UserRolesItem): the location's back-office roles.
// Search, "Add Role", the table (Name with a lock on the system role, Description, Users, Edit/Delete), and the role
// dialog: Role Details (Name, Description) and Permissions as toggles grouped by category (legacy
// UserPermissionToggles). The system role (Super Admin) opens read-only with the legacy banner. Deleting a role moves
// its users to Super Admin, as legacy did, and the dialog says how many.
import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { MATERIAL_LOCK } from '../../shell/material-icons.tsx';
import type { PermissionKey, SettingKey } from '@radish/contracts';
import { Banner, Button, DataTable, DeleteDialog, Dialog, EmptyState, TableActionButton, LoadingState, SECTION_HEADING, SearchField, TextField, Toggle, legacySearchMatches, useToast, DeleteIcon, EditIcon } from '@radish/ui-web';
import { useActiveLocation } from '../../app/location.tsx';
import { ListHeader, LoadError } from '../../shell/page.tsx';
import { useDeleteRole, useRoles, useUpsertRole, type Role } from './data.ts';
import { useEffectiveSettings } from '../../data/settings.ts';
import { roleDefaults, rolePermissionGroups, roleToggleVisible, toggled } from './role-permissions.ts';
import { DELETE_PICTURE } from '../../shell/delete-picture.ts';

export { rolePermissionGroups } from './role-permissions.ts';

type Editing = { readonly mode: 'new' } | { readonly mode: 'edit'; readonly role: Role };

export function UserRolesPage() {
  const location = useActiveLocation();
  const toast = useToast();
  const roles = useRoles(location.locationId);
  const remove = useDeleteRole(location.locationId);
  const [term, setTerm] = useState('');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);

  const rows = useMemo(() => (roles.data?.roles ?? []).filter((r) => legacySearchMatches(term, r.name)), [roles.data, term]);
  const columns = useMemo<ColumnDef<Role, never>[]>(() => [
    {
      id: 'name', header: 'Name', accessorFn: (r) => r.name.toLowerCase() as never,
      // Legacy: the system role's name opens nothing and carries q-icon lock (16 px, grey-6); any other opens its editor.
      cell: (c) => (c.row.original.isSystem ? (
        <span className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="shrink-0 text-grey-6" role="img" aria-label="System role" data-testid="system-role-lock"><path d={MATERIAL_LOCK} /></svg>
          {c.row.original.name}
        </span>
      ) : (
        <button type="button" className="text-left" onClick={() => setEditing({ mode: 'edit', role: c.row.original })}>{c.row.original.name}</button>
      )),
    },
    { id: 'description', header: 'Description', accessorFn: (r) => (r.description ?? '') as never, cell: (c) => c.row.original.description ?? '-' },
    { id: 'users', header: 'Users', accessorFn: (r) => r.memberCount as never, meta: { align: 'center' }, cell: (c) => c.row.original.memberCount },
    {
      id: 'actions', header: () => <span className="sr-only">Actions</span>, enableSorting: false, meta: { align: 'right', width: '100px' },
      cell: (c) => (c.row.original.isSystem ? null : (
        <span className="inline-flex">
          <TableActionButton label={`Edit Role ${c.row.original.name}`} onClick={() => setEditing({ mode: 'edit', role: c.row.original })}><EditIcon /></TableActionButton>
          <TableActionButton label={`Delete Role ${c.row.original.name}`} onClick={() => setDeleting(c.row.original)}><DeleteIcon /></TableActionButton>
        </span>
      )),
    },
  ], []);

  const count = deleting?.memberCount ?? 0;
  return (
    // Legacy's ui-settings-block (908 px, centred) and SettingsHeader: the search (in a `.col`) fills the first group up
    // to Add Role, with q-mb-lg's 24 px above the table.
    <div className="mx-auto w-full max-w-[940px] px-4 pb-4 pt-9 max-[1024px]:pt-6" data-testid="user-roles-page">
      <div className="pb-2" data-testid="user-roles-header">
        <ListHeader
          settings
          start={<div className="min-w-0 flex-1"><SearchField value={term} onValueChange={setTerm} placeholder="Search roles..." className="min-[600px]:w-full!" /></div>}
          end={<Button onClick={() => setEditing({ mode: 'new' })} data-testid="add-role">Add Role</Button>}
        />
      </div>
      {roles.isPending ? <LoadingState /> : roles.isLoadingError ? <LoadError error={roles.error} /> : (
        <DataTable
          caption="User Roles"
          testId="user-roles-table"
          columns={columns}
          data={rows}
          getRowId={(r) => r.roleId}
          initialSort={[{ id: 'name', desc: false }]}
          empty={<EmptyState uiNoDataPhone illustration="/assets/missing-states/light/missing_state_employees.svg" darkIllustration="/assets/missing-states/dark/missing_state_employees_dark.svg" title="No roles have been created yet" description="Create roles to control which backoffice pages users can access" />}
        />
      )}
      <RoleDialog editing={editing} onClose={() => setEditing(null)} />
      <DeleteDialog
        open={deleting !== null}
        onOpenChange={(o) => { if (!o) setDeleting(null); }}
        title="Delete Role"
        model="role"
        {...DELETE_PICTURE}
        // Legacy deleteRoleDialog: one sentence, naming how many users move to Super Admin when any hold the role.
        message={<span data-testid="delete-role-sentence">{count > 0 ? `This role is assigned to ${String(count)} user${count > 1 ? 's' : ''}. They will be reassigned to the Super Admin role. Are you sure you want to delete this role?` : 'Are you sure you want to delete this role?'}</span>}
        busy={remove.isPending}
        onConfirm={() => {
          if (deleting !== null) remove.mutate(deleting.roleId, { onSuccess: () => { setDeleting(null); toast.success('User role deleted successfully.'); }, onError: (e) => toast.error(e.message) });
        }}
      />
    </div>
  );
}

function RoleDialog({ editing, onClose }: { readonly editing: Editing | null; readonly onClose: () => void }) {
  const location = useActiveLocation();
  const toast = useToast();
  const upsert = useUpsertRole(location.locationId);
  const groups = useMemo(rolePermissionGroups, []);
  const lockedKeys = useMemo(() => groups.flatMap((g) => g.toggles.filter((t) => t.locked).map((t) => t.key)), [groups]);
  const role = editing?.mode === 'edit' ? editing.role : null;
  const locked = role?.isSystem === true;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [granted, setGranted] = useState<ReadonlySet<PermissionKey>>(new Set());
  const [errors, setErrors] = useState<{ name?: string; permissions?: string }>({});
  useEffect(() => {
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    // A new role starts on legacy's defaults (UserRolesStore: `{ ...permissionsStore.defaults }`).
    setGranted(new Set(role === null ? roleDefaults() : role.permissions));
    setErrors({});
  }, [editing, role]);

  const save = () => {
    const next = { ...(name.trim() === '' ? { name: 'Role name is required' } : {}), ...(granted.size === 0 ? { permissions: 'Choose at least one permission' } : {}) };
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    upsert.mutate({ roleId: role?.roleId ?? null, name, description, permissions: [...new Set([...granted, ...lockedKeys])] }, {
      onSuccess: () => { onClose(); toast.success(role === null ? 'User role created successfully.' : 'User role updated successfully.'); },
      onError: (e) => toast.error(e.message),
    });
  };
  const toggle = (key: PermissionKey, on: boolean) => setGranted(toggled(granted, key, on));

  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={locked ? 'View Role' : role === null ? 'Add Role' : 'Edit Role'}
      phoneFooterFill
      testId="role-dialog"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {locked ? null : <Button onClick={save} loading={upsert.isPending}>{role === null ? 'Create' : 'Update'}</Button>}
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (!locked) save(); }}>
        {locked ? <Banner tone="locked">This is a system role and cannot be modified.</Banner> : null}
        {/* Legacy UserRolesItem.vue's UiSectionHeadings: Role Details q-mb-md, Permissions q-mt-lg (the gap-4 and pt-2 here) (B1339). */}
        <h3 className={SECTION_HEADING}>Role Details</h3>
        <div className="grid gap-4 min-[1024px]:grid-cols-2">
          <TextField label="Name" value={name} onValueChange={setName} error={errors.name} readOnly={locked} />
          <TextField label="Description" value={description} onValueChange={setDescription} readOnly={locked} />
        </div>
        <h3 className={`pt-2 ${SECTION_HEADING}`}>Permissions</h3>
        {errors.permissions === undefined ? null : <p role="alert" className="text-[13px] text-negative">{errors.permissions}</p>}
        <PermissionToggles locationId={location.locationId} granted={granted} readOnly={locked} onToggle={toggle} />
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

/**
 * Legacy UserPermissionToggles: the role's permissions by category, children indented under their parent and off with
 * it, the locked keys always on. `readOnly` is legacy's `disabled` (the system role, and Assign User Role's preview).
 */
/** Legacy ArrowNextIcon (`tw-pl-2 tw-mb-1 tw-mr-3`): a 12 px return arrow in #6F7070 before a child permission. */
function ChildArrow() {
  return (
    <span className="mr-3 mb-1 flex h-[21px] items-center pl-2" aria-hidden data-testid="permission-child-arrow">
      <svg width="12" height="12" viewBox="0 0 14 12" fill="none">
        <path fill="#6F7070" d="M14 7.584c0-.022-.004-.048-.009-.07 0-.026 0-.048-.009-.074-.004-.022-.013-.039-.017-.061-.009-.022-.013-.048-.022-.07-.009-.026-.026-.048-.039-.07-.009-.017-.018-.039-.031-.057-.022-.03-.048-.061-.07-.087l-.017-.026L10.067 3.4c-.302-.297-.788-.293-1.081.008-.297.301-.293.785.009 1.078l2.384 2.343H3.014c-.818 0-1.483-.663-1.483-1.479V.764C1.531.34 1.19 0 .766 0 .341 0 0 .34 0 .764v4.577c0 1.658 1.352 3.007 3.014 3.007h8.352L8.982 10.69c-.302.297-.306.782-.009 1.078.149.153.346.227.547.227.201 0 .389-.074.538-.218l3.714-3.652.022-.026c.026-.026.053-.057.07-.087.013-.017.022-.039.031-.057.013-.022.026-.044.035-.07.009-.022.013-.048.022-.07.004-.022.013-.039.017-.061.004-.026.004-.048.009-.074 0-.022.009-.048.009-.07L14 7.584Z" />
      </svg>
    </span>
  );
}

export function PermissionToggles({ locationId, granted, readOnly, onToggle }: { readonly locationId: string; readonly granted: ReadonlySet<PermissionKey>; readonly readOnly: boolean; readonly onToggle?: (key: PermissionKey, on: boolean) => void }) {
  const groups = useMemo(rolePermissionGroups, []);
  const settings = useEffectiveSettings(locationId).settings;
  const isOn = (key: SettingKey) => settings?.get(key) === true;
  return (
    <div data-testid="permission-toggles">
      {groups.map((g) => ({ ...g, toggles: g.toggles.filter((t) => roleToggleVisible(t, isOn)) })).filter((g) => g.toggles.length > 0).map((g) => (
        // Legacy (B1470): each category `q-mb-lg` under a text-subtitle2 title on a 22 px line; each row `q-mt-sm` with
        // the 16 px column gutter above its 21 px label in the secondary ink.
        <fieldset key={g.category} className="mb-6" data-testid="permission-category">
          <legend className="text-[14px] leading-[22px] font-bold text-text-2">{g.category}</legend>
          {g.toggles.map((p) => (
            <div key={p.key} className="mt-2 flex items-center justify-between gap-4 pt-4" data-testid="permission-row">
              <label htmlFor={`perm-${p.key}`} className={`flex items-center text-[14px] leading-[21px] ${p.locked || readOnly ? 'text-text-3' : 'text-text-2'}`}>
                {p.parent === null ? null : <ChildArrow />}
                {p.locked ? <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2 shrink-0 text-grey-6" aria-hidden data-testid="permission-lock"><path d={MATERIAL_LOCK} /></svg> : null}
                {p.label}
              </label>
              <Toggle
                id={`perm-${p.key}`}
                label={p.label}
                checked={p.locked || granted.has(p.key)}
                disabled={readOnly || p.locked || (p.parent !== null && !granted.has(p.parent))}
                onCheckedChange={(on) => onToggle?.(p.key, on)}
              />
            </div>
          ))}
        </fieldset>
      ))}
    </div>
  );
}
